import type { EntityIconVariant } from '../../components/sidebar/EntityIcon';
import type { DrillLocation, ExplorationSecondary, ViewMode } from '../../types/exploration';
import { isDrillLocationEmpty, structuralPathStrings } from '../drill/drillLocation';
import { effectiveSecondary } from '../secondaryGrouping';

export interface ListingGroupIconTypeOptions {
  secondary: ExplorationSecondary;
  location: DrillLocation;
  groupByAccount: boolean;
  view: ViewMode;
  tagKey: string;
}

/** Icon / hierarchy type for a row in the sidebar group-bars card at the current drill depth. */
export function iconTypeForListingGroup(
  groupName: string,
  opts: ListingGroupIconTypeOptions
): EntityIconVariant | null {
  const { secondary, groupByAccount, view, tagKey, location } = opts;
  const groupingSecondary = effectiveSecondary(secondary, tagKey);

  if (groupByAccount && isDrillLocationEmpty(location)) {
    return 'account';
  }

  // Focused Unsupported / Pending-setup without a reason yet — sidebar rows are
  // reason labels, not structural entities (node groups / clusters).
  if (location.focusedActivationKey && !location.reasonLabel) {
    return null;
  }

  const path = structuralPathStrings(location, { groupByAccount: false });

  if (groupingSecondary === 'region') return path.length === 0 ? 'region' : 'volume';
  if (groupingSecondary === 'tag') return path.length === 0 ? 'tag' : 'volume';
  if (groupingSecondary === 'none') return null;

  if (path.length === 0) {
    if (groupName === 'EC2 Instances') return 'ec2';
    if (groupName === 'Unattached') return 'unattached';
    return 'cluster';
  }

  const top = path[0];
  if (top === 'Unattached') return null;
  if (top === 'EC2 Instances') {
    if (path.length === 1) return view === 'activation' ? null : 'instance';
    return null;
  }
  if (path.length === 1) return 'nodeGroup';
  if (path.length === 2) return 'instance';
  return null;
}
